import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useStepLogs } from "@/lib/hooks/useDeploymentLogs";
import { cn } from "@/lib/utils";
import { formatDuration, intervalToDuration } from "date-fns";
import type { DeploymentStep as DeploymentStepType } from "../../../../../../schemas/codegen";
import { StepDuration } from "./StepDuration";
import { StepIcon } from "./StepIcon";

// Utility function to format duration using date-fns
export const formatStepDuration = (milliseconds: number): string => {
  // Handle zero or very small durations
  if (milliseconds < 1000) {
    return "0 s";
  }

  const duration = intervalToDuration({ start: 0, end: milliseconds });

  // Use date-fns formatDuration for consistent formatting
  return formatDuration(duration, {
    format: ["hours", "minutes", "seconds"],
    delimiter: " ",
  })
    .replace(/hours?/, "h")
    .replace(/minutes?/, "m")
    .replace(/seconds?/, "s");
};

interface DeploymentStepProps {
  step: DeploymentStepType;
  deploymentId: string;
}

export const DeploymentStep = ({ step, deploymentId }: DeploymentStepProps) => {
  const logs = useStepLogs(deploymentId, step.id);
  const hasLogs = logs.length > 0;

  // For pending steps, disable the entire AccordionItem
  const isPending = step.status === "pending";
  const canExpand = !isPending && hasLogs;

  return (
    <AccordionItem
      value={step.id}
      disabled={isPending}
      className="border-b last:border-b-0"
    >
      <AccordionTrigger
        className={cn("px-3", {
          "hover:bg-muted/20": canExpand,
          "text-destructive": step.status === "error",
          "text-muted-foreground":
            step.status === "skipped" ||
            step.status === "cancelled" ||
            step.status === "pending",
        })}
        disabled={!canExpand}
      >
        <div className="flex items-center gap-3 flex-1">
          <StepIcon status={step.status} />
          <span>{step.name}</span>
          <div className="ml-auto mr-4 text-sm text-muted-foreground">
            <StepDuration
              status={step.status}
              startedAt={step.startedAt ?? undefined}
              finishedAt={step.finishedAt ?? undefined}
            />
          </div>
        </div>
      </AccordionTrigger>
      {canExpand && (
        <AccordionContent>
          <div className="bg-muted/50 rounded p-3 text-sm mx-3">
            <pre className="text-sm p-3 rounded-md overflow-x-auto text-muted-foreground">
              {logs}
            </pre>
          </div>
        </AccordionContent>
      )}
    </AccordionItem>
  );
};
